/**
 * Darkens a given hex color by a specified amount.
 *
 * This function takes a hex color string and an amount by which to darken the color.
 * It returns the darkened color as a hex string.
 *
 * @param color - The hex color string to be darkened. It can start with or without a '#'.
 * @param amount - The amount by which to darken the color. This value is subtracted from each RGB component.
 * @returns - The darkened hex color string. The returned string will include a '#' if the input color included it.
 *
 * @example
 * // Darken the color '#ff0000' by 20 units
 * const darkenedColor = darkenColor('#ff0000', 20);
 * console.log(darkenedColor); // Outputs: '#e60000'
 */
export function darkenColor(color: string, amount: number): string {
  const usePound = color.startsWith('#')
  const num = parseInt(color.slice(1), 16)

  let r = Math.floor(num / (256 * 256)) - amount
  let g = Math.floor((num / 256) % 256) - amount
  let b = (num % 256) - amount

  r = r < 0 ? 0 : r
  g = g < 0 ? 0 : g
  b = b < 0 ? 0 : b

  const rHex = r.toString(16).padStart(2, '0')
  const gHex = g.toString(16).padStart(2, '0')
  const bHex = b.toString(16).padStart(2, '0')

  return (usePound ? '#' : '') + rHex + gHex + bHex
}

/**
 * Reduces the saturation of a given hex color by a specified amount.
 *
 * This function takes a hex color string and a percentage by which to reduce the saturation.
 * It converts the color to HSL, reduces the saturation, and converts back to hex.
 *
 * @param color - The hex color string to desaturate. It can start with or without a '#'.
 * @param amount - The percentage (0-100) by which to reduce saturation.
 * @returns - The desaturated hex color string. The returned string will include a '#' if the input color included it.
 *
 * @example
 * // Reduce saturation of '#ff0000' by 50%
 * const desaturatedColor = desaturateColor('#ff0000', 50);
 * console.log(desaturatedColor); // Outputs a less saturated red
 */
export function desaturateColor(color: string, amount: number): string {
  const usePound = color.startsWith('#')
  const hex = usePound ? color.slice(1) : color

  // Convert hex to RGB
  const r = parseInt(hex.slice(0, 2), 16) / 255
  const g = parseInt(hex.slice(2, 4), 16) / 255
  const b = parseInt(hex.slice(4, 6), 16) / 255

  // Find greatest and smallest channel values
  const cmin = Math.min(r, g, b)
  const cmax = Math.max(r, g, b)
  const delta = cmax - cmin

  let h = 0
  let s = 0
  let l = 0

  // Calculate hue
  if (delta === 0) h = 0
  else if (cmax === r) h = ((g - b) / delta) % 6
  else if (cmax === g) h = (b - r) / delta + 2
  else h = (r - g) / delta + 4

  h = Math.round(h * 60)
  if (h < 0) h += 360

  // Calculate lightness
  l = (cmax + cmin) / 2

  // Calculate saturation
  s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1))

  // Reduce saturation by amount%
  s = Math.max(0, s * (1 - amount / 100))

  // Convert back to RGB
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = l - c / 2
  let rr = 0
  let gg = 0
  let bb = 0

  if (0 <= h && h < 60) {
    rr = c
    gg = x
    bb = 0
  } else if (60 <= h && h < 120) {
    rr = x
    gg = c
    bb = 0
  } else if (120 <= h && h < 180) {
    rr = 0
    gg = c
    bb = x
  } else if (180 <= h && h < 240) {
    rr = 0
    gg = x
    bb = c
  } else if (240 <= h && h < 300) {
    rr = x
    gg = 0
    bb = c
  } else if (300 <= h && h < 360) {
    rr = c
    gg = 0
    bb = x
  }

  // Convert back to hex
  const r2 = Math.round((rr + m) * 255)
  const g2 = Math.round((gg + m) * 255)
  const b2 = Math.round((bb + m) * 255)

  const rHex = r2.toString(16).padStart(2, '0')
  const gHex = g2.toString(16).padStart(2, '0')
  const bHex = b2.toString(16).padStart(2, '0')

  return (usePound ? '#' : '') + rHex + gHex + bHex
}

/**
 * Adds alpha to a given hex color.
 *
 * This function takes a hex color string and an alpha value to be added to the color.
 * It returns the color as a hex string with alpha.
 *
 * @param color - The hex color string to which alpha is to be added. It can start with or without a '#'.
 * @param alpha - The alpha value to be added to the color. This value is a decimal between 0 and 1.
 * @returns - The hex color string with alpha. The returned string will include a '#' if the input color included it.
 *
 * @example
 * // Add alpha 0.5 to the color '#ff0000'
 * const colorWithAlpha = addAlphaToColor('#ff0000', 0.5);
 * console.log(colorWithAlpha); // Outputs: '#ff000080'
 */
export function addAlphaToColor(
  color: string | undefined,
  alpha: number | undefined
): string | undefined {
  if (color === undefined || alpha === undefined) {
    return undefined
  }

  const usePound = color.startsWith('#')
  const hex = usePound ? color.slice(1) : color

  // Check if the color already has an alpha component
  if (hex.length === 8) {
    // Replace the existing alpha component
    const newHex = hex.slice(0, 6)
    const a = Math.round(alpha * 255)
    const aHex = a.toString(16).padStart(2, '0')
    return (usePound ? '#' : '') + newHex + aHex
  }

  const num = parseInt(hex, 16)

  const r = Math.floor(num / (256 * 256))
  const g = Math.floor((num / 256) % 256)
  const b = num % 256

  const a = Math.round(alpha * 255)

  const rHex = r.toString(16).padStart(2, '0')
  const gHex = g.toString(16).padStart(2, '0')
  const bHex = b.toString(16).padStart(2, '0')
  const aHex = a.toString(16).padStart(2, '0')

  return (usePound ? '#' : '') + rHex + gHex + bHex + aHex
}

/**
 * Blends two colors together with specified intensities.
 * This matches the algorithm used in other NotePlan apps.
 *
 * @param color1 - First hex color to blend
 * @param intensity1 - Intensity/weight of first color (0-1)
 * @param color2 - Second hex color to blend
 * @param intensity2 - Intensity/weight of second color (0-1)
 * @returns - Blended hex color string
 */
export function blendColors(
  color1: string,
  intensity1 = 0.5,
  color2: string,
  intensity2 = 0.5
): string {
  const usePound = color1.startsWith('#')
  const hex1 = usePound ? color1.slice(1) : color1
  const hex2 = color2.startsWith('#') ? color2.slice(1) : color2

  // Parse colors
  const r1 = parseInt(hex1.slice(0, 2), 16) / 255
  const g1 = parseInt(hex1.slice(2, 4), 16) / 255
  const b1 = parseInt(hex1.slice(4, 6), 16) / 255

  const r2 = parseInt(hex2.slice(0, 2), 16) / 255
  const g2 = parseInt(hex2.slice(2, 4), 16) / 255
  const b2 = parseInt(hex2.slice(4, 6), 16) / 255

  // Calculate blend ratios
  const total = intensity1 + intensity2
  const l1 = intensity1 / total
  const l2 = intensity2 / total

  // Blend RGB components
  const r = Math.round((l1 * r1 + l2 * r2) * 255)
  const g = Math.round((l1 * g1 + l2 * g2) * 255)
  const b = Math.round((l1 * b1 + l2 * b2) * 255)

  const rHex = r.toString(16).padStart(2, '0')
  const gHex = g.toString(16).padStart(2, '0')
  const bHex = b.toString(16).padStart(2, '0')

  return (usePound ? '#' : '') + rHex + gHex + bHex
}

/**
 * Calculates the relative luminance of a color.
 * Used to determine if a color is light or dark.
 *
 * @param color - Hex color string
 * @returns - Luminance value between 0 (black) and 1 (white)
 */
export function getLuminance(color: string): number {
  const hex = color.startsWith('#') ? color.slice(1) : color

  const r = parseInt(hex.slice(0, 2), 16) / 255
  const g = parseInt(hex.slice(2, 4), 16) / 255
  const b = parseInt(hex.slice(4, 6), 16) / 255

  // Apply gamma correction
  const rLinear = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4)
  const gLinear = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4)
  const bLinear = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4)

  // Calculate relative luminance
  return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear
}

/**
 * Determines if a color is considered light based on its luminance.
 * 
 * @param color - Hex color string
 * @returns - True if the color is light (luminance > 0.6)
 */
export function isLightColor(color: string): boolean {
  return getLuminance(color) > 0.6
}

export const DEFAULT_CALENDAR_COLOR = '#0091f8' // same as --blue-noteplan-hsl in index.css
